import { useState } from 'react';

import { useDepositLedger, type DepositLedgerRow, type DepositLedgerView } from '@/api/deposits';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { humanizeEnum } from '@core/format/text';

const VIEWS: { value: DepositLedgerView; label: string; hint: string }[] = [
  {
    value: 'Holding',
    label: 'Holding',
    hint: 'Money in the shop against garments that have not been settled.',
  },
  {
    value: 'Owing',
    label: 'Owing',
    hint: 'Settled, and the customer has not been paid back. Longest wait first.',
  },
  { value: 'All', label: 'All', hint: 'Every deposit ever taken, newest first.' },
];

/**
 * Section 9's deposit register — what the shop is holding, and who it is keeping waiting.
 *
 * <p>
 * Two of §9's four reports, delivered as a screen rather than a report: "deposit liability
 * outstanding (money you are holding and owe back)" and "pending refunds ageing". Read-only, because
 * every operation on a deposit already has a home — it is taken at the counter, settled from the
 * booking, and refunded through the payment drawer. What did not exist was anywhere to see the
 * total.
 * </p>
 *
 * <p>
 * <strong>The totals do not move with the tab.</strong> They are the shop's exposure, and a figure
 * that changed when somebody switched view would be read as the tab's subtotal and quoted as the
 * shop's.
 * </p>
 */
export function DepositsPage() {
  const [view, setView] = useState<DepositLedgerView>('Holding');

  const ledger = useDepositLedger(view);
  const totals = ledger.data?.totals;

  const columns: DataViewColumn<DepositLedgerRow>[] = [
    {
      title: 'Customer',
      dataIndex: 'customerName',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.customerName}</div>
          <div className="font-mono-nums text-[13px] text-default">
            {row.customerMobile}
            {row.bookingNumber ? ` · ${row.bookingNumber}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Deposit',
      dataIndex: 'state',
      render: (_, row) =>
        row.state === 'Held' ? (
          <StatusBadge tone="brand">Held</StatusBadge>
        ) : row.state === 'Forfeited' ? (
          <StatusBadge tone="negative">Forfeited</StatusBadge>
        ) : row.state === 'PartRefunded' ? (
          <StatusBadge tone="warning">Part refunded</StatusBadge>
        ) : row.state === 'AppliedToBalance' ? (
          // All of it paid what the customer owed — not forfeited, and not taxed again.
          <StatusBadge tone="neutral">Paid the bill</StatusBadge>
        ) : (
          <StatusBadge tone="positive">{humanizeEnum(row.state)}</StatusBadge>
        ),
    },
    {
      title: 'Held',
      dataIndex: 'held',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {row.held === null ? '—' : formatAmount(row.held)}
        </span>
      ),
    },
    {
      title: 'Due back',
      dataIndex: 'refundDue',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {/* Null and zero mean different things: "not settled yet" against "nothing owed". A dash
              for both would read as the second on a deposit nobody has worked out. */}
          {row.refundDue === null ? '—' : formatAmount(row.refundDue)}
        </span>
      ),
    },
    {
      title: 'Still owed',
      dataIndex: 'refundOutstanding',
      align: 'right',
      render: (_, row) => (
        <span
          className={`font-mono-nums text-[13px] ${
            row.refundOutstanding > 0 ? 'font-semibold text-danger' : ''
          }`}
        >
          {row.refundOutstanding > 0 ? formatAmount(row.refundOutstanding) : '—'}
        </span>
      ),
    },
    {
      title: 'Waiting',
      dataIndex: 'daysWaiting',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {row.daysWaiting === null ? '—' : `${row.daysWaiting}d`}
        </span>
      ),
    },
    {
      title: 'Settled',
      dataIndex: 'settledOn',
      hideOnCard: true,
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {row.settledOn ? formatDate(row.settledOn) : '—'}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Deposits"
        subtitle="What the shop is holding, and who it is keeping waiting. Neither figure is stored — both are read back off the payment ledger."
      />

      <div className="mb-3 grid gap-3 sm:grid-cols-3 lg:mb-6">
        <Card>
          <div className="text-[13px] text-default">Holding now</div>
          <div className="font-mono-nums mt-1 text-xl font-semibold text-gray-900">
            {formatAmount(totals?.holding ?? 0)}
          </div>
          <div className="mt-0.5 text-[13px] text-default">
            across {totals?.bookingsHolding ?? 0} booking
            {(totals?.bookingsHolding ?? 0) === 1 ? '' : 's'}
          </div>
        </Card>

        <Card>
          <div className="text-[13px] text-default">Owed back</div>
          <div
            className={`font-mono-nums mt-1 text-xl font-semibold ${
              (totals?.outstanding ?? 0) > 0 ? 'text-danger' : 'text-gray-900'
            }`}
          >
            {formatAmount(totals?.outstanding ?? 0)}
          </div>
          <div className="mt-0.5 text-[13px] text-default">
            {totals?.bookingsOwing ?? 0} customer{(totals?.bookingsOwing ?? 0) === 1 ? '' : 's'}
            {(totals?.oldestWaitingDays ?? 0) > 0 ? ` · longest ${totals?.oldestWaitingDays}d` : ''}
          </div>
        </Card>

        <Card>
          <div className="text-[13px] text-default">Forfeited to date</div>
          <div className="font-mono-nums mt-1 text-xl font-semibold text-gray-900">
            {formatAmount(totals?.forfeitedToDate ?? 0)}
          </div>
          {/* Said out loud because it is the one figure here that is *not* a liability, and a
              reader scanning three money tiles will otherwise add all three together. */}
          <div className="mt-0.5 text-[13px] text-default">already the shop&rsquo;s, not owed</div>
        </Card>
      </div>

      {(totals?.bookingsOwing ?? 0) > 0 && view !== 'Owing' && (
        <div className="mb-3 lg:mb-6">
          <Notice
            tone="warning"
            title={`${totals?.bookingsOwing} refund${totals?.bookingsOwing === 1 ? '' : 's'} not handed back`}
          >
            A deposit settled is not a deposit returned. Open the Owing tab to see who has been
            waiting longest — it is the only list here that turns into a complaint.
          </Notice>
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <div className="flex flex-wrap gap-1">
            {VIEWS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setView(option.value)}
                aria-pressed={view === option.value}
                className={`rounded border px-2.5 py-1 text-[13px] ${
                  view === option.value
                    ? 'border-primary bg-primary text-white'
                    : 'border-border-color text-default hover:text-dark'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <span className="text-[13px] text-default">
            {VIEWS.find((option) => option.value === view)?.hint}
          </span>
        </div>

        <DataView<DepositLedgerRow>
          columns={columns}
          dataSource={ledger.data?.rows ?? []}
          loading={ledger.isLoading}
          rowKey="bookingId"
          empty={
            <EmptyState
              icon="icon icon-wallet"
              title={
                view === 'Owing'
                  ? 'Nobody is waiting'
                  : view === 'Holding'
                    ? 'Holding nothing'
                    : 'No deposit has been taken yet'
              }
              hint={
                view === 'Owing'
                  ? 'Every settled deposit has been handed back.'
                  : view === 'Holding'
                    ? 'A deposit appears here as soon as one is taken on a booking.'
                    : 'Deposits are taken at the counter when a booking is confirmed.'
              }
            />
          }
        />
      </Card>
    </>
  );
}
